import { useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { CONFIG_STORAGE_KEY } from '@/services/tenantService';

// Mapeamento de padrões de rota para títulos amigáveis
const ROUTE_TITLES = {
  '/': 'Login',
  '/auth/login': 'Login',
  '/auth/login/password': 'Login com Senha',
  '/auth/forgot-password': 'Esqueci a Senha',
  '/auth/signup': 'Cadastro',
  '/auth/signup-otp': 'Verificação OTP',
  '/auth/create-password': 'Criar Senha',
  '/auth/success': 'Cadastro Realizado',
  '/auth/logout': 'Logout',
  '/home': 'Home',
  '/dashboard': 'Home',
  '/profile': 'Perfil',
  '/statement': 'Extrato',
  '/statement/:walletId': 'Extrato da Carteira',
  '/wallets': 'Carteiras',
  '/select-destination': 'Selecionar Destino',
  '/language': 'Idioma',
  '/faq': 'FAQ',
  '/faq/logged': 'FAQ',
  '/tour-guide': 'Tour Guide',
  '/benefits': 'Benefícios',
  '/benefits-history': 'Histórico de Benefícios',
  '/pay-bill': 'Pagamento de Conta',
  '/pay-bill-history': 'Histórico de Pagamentos',
  '/pay-bill-receipt/:id': 'Comprovante de Pagamento',
  '/pay-bill-processing/:id': 'Processando Pagamento',
  '/pay-bill-error/:id': 'Erro no Pagamento',
  '/pay-bill-pix': 'Pagamento via Pix',
  '/pay-bill-confirmation': 'Confirmação de Pagamento',
  '/stamp-redemption': 'Resgate de Stamp',
  '/stamp/:id': 'Stamp',
  '/stamp-redemption-success/:id': 'Resgate Realizado',
  '/stamp/:id/success-info': 'Resgate Realizado',
  '/stamp/:id/error': 'Erro no Resgate',
  '/accepted-terms': 'Termos Aceitos',
  '/terms/:id': 'Termos',
  '/delete-account': 'Excluir Conta',
  '/mechanics': 'Mecânica',
  '/winners': 'Vencedores',
  '/how-to-participate': 'Como Participar',
  '/campaigns': 'Campanhas',
  '/campaigns-list': 'Lista de Campanhas',
  '/campaign/:id': 'Detalhe da Campanha',
  '/performance': 'Performance',
  '/performance-new/:id': 'Performance Detalhada',
  '/performance-list/:id': 'Lista de Performance',
  '/ranking': 'Ranking',
  '/segmentation': 'Segmentação',
  '/training': 'Treinamentos',
  '/training/:id': 'Detalhe do Treinamento',
  '/training/:id/quiz': 'Quiz do Treinamento',
  '/quiz': 'Quizzes',
  '/quiz/:id': 'Detalhe do Quiz',
  '/quiz/:id/questions': 'Perguntas do Quiz',
  '/survey': 'Pesquisas',
  '/survey/:id': 'Detalhe da Pesquisa',
  '/survey/:id/questions': 'Perguntas da Pesquisa',
  '/protected-training': 'Treinamentos Protegidos',
  '/protected-training/:id': 'Detalhe do Treinamento Protegido',
  '/protected-training/:id/quiz': 'Quiz do Treinamento Protegido',
  '/news': 'Notícias',
  '/news/:slug': 'Notícia',
  '/news/category/:categoryId': 'Categoria de Notícias',
  '/news/author/:authorId': 'Autor',
  '/chatbot/:id': 'Chatbot',
  '/redirect/revvo': 'Redirect Revvo',
  '/redirect/marketplace': 'Redirect Marketplace',
  '/campaign-simulator': 'Simulador de Campanha',
  '/campaign-simulator-2': 'Simulador de Campanha 2',
  '/error': 'Erro',
  '/404': 'Página não Encontrada',
  '/wait': 'Aguardando',
  '/sso-allianz': 'SSO',
};

const getPageTitle = (pathname) => {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  for (const [pattern, title] of Object.entries(ROUTE_TITLES)) {
    if (matchPath(pattern, pathname)) return title;
  }

  return pathname;
};

/**
 * Componente responsável por rastrear mudanças de rota no Google Analytics.
 * Deve ser renderizado dentro do RouterProvider.
 */
export const GAPageTracker = () => {
  const location = useLocation();
  const previousPath = useRef('');

  useEffect(() => {
    let config;
    try {
      const configStorage = sessionStorage?.getItem(CONFIG_STORAGE_KEY) || '{}';
      config = JSON.parse(configStorage);
    } catch (error) {
      console.error('Error loading config for GA page tracking:', error);
      return;
    }

    if (!config?.FEATURE_FLAGS?.GA_ID) return;

    const currentPath = location.pathname + location.search;

    if (previousPath.current === currentPath) return;

    const timeoutId = setTimeout(() => {
      if (!window.gtag) return;

      const pageTitle = getPageTitle(location.pathname);
      const pageLocation = window.location.href;

      window.gtag('event', 'page_view', {
        page_title: pageTitle,
        page_location: pageLocation,
        page_path: currentPath,
      });

      previousPath.current = currentPath;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location]);

  return null;
};
